import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Footer } from '../components/landing/Footer'
import { Navbar } from '../components/landing/Navbar'
import { getFeaturesForPlan, PLANS, PRICING_FEATURES, type Plan as PricingPlan } from '../constants/pricingData'

type ScrollRevealProps = {
  children: React.ReactNode
  className?: string
  delay?: string
  repeat?: boolean
}

const ScrollReveal: React.FC<ScrollRevealProps> = ({
  children,
  className = '',
  delay = '',
  repeat = false,
}) => {
  const [isVisible, setIsVisible] = useState(false)
  const domRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setIsVisible(true)
            if (!repeat) {
              observer.unobserve(entry.target)
            }
          } else if (repeat) {
            setIsVisible(false)
          }
        })
      },
      { threshold: 0.1 }
    )

    const current = domRef.current
    if (current) {
      observer.observe(current)
    }

    return () => {
      if (current) {
        observer.unobserve(current)
      }
    }
  }, [repeat])

  return (
    <div ref={domRef} className={`reveal ${className} ${delay} ${isVisible ? 'active' : ''}`}>
      {children}
    </div>
  )
}

const BracketButton: React.FC<{
  children: React.ReactNode
  className?: string
  onClick?: () => void
}> = ({ children, className = '', onClick }) => {
  const [isVisible, setIsVisible] = useState(false)
  const [playOnLoad, setPlayOnLoad] = useState(false)
  const [hasIntroCompleted, setHasIntroCompleted] = useState(false)
  const domRef = useRef<HTMLButtonElement>(null)
  const hasStartedIntro = useRef(false)

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          setIsVisible(entry.isIntersecting)
        })
      },
      { threshold: 0.5 }
    )

    const current = domRef.current
    if (current) {
      observer.observe(current)
    }

    return () => {
      if (current) {
        observer.unobserve(current)
      }
    }
  }, [])

  useEffect(() => {
    const current = domRef.current
    if (!current) {
      return
    }

    const revealDelayMs = 800
    let activateTimer = 0
    let resetTimer = 0

    const startIntroAnimation = () => {
      if (hasStartedIntro.current) {
        return
      }

      hasStartedIntro.current = true
      setPlayOnLoad(true)

      resetTimer = window.setTimeout(() => {
        setPlayOnLoad(false)
        setHasIntroCompleted(true)
      }, 850)
    }

    const revealParent = current.closest('.reveal')

    if (!revealParent) {
      activateTimer = window.setTimeout(startIntroAnimation, revealDelayMs)

      return () => {
        window.clearTimeout(activateTimer)
        window.clearTimeout(resetTimer)
      }
    }

    const classObserver = new MutationObserver(() => {
      if (revealParent.classList.contains('active')) {
        activateTimer = window.setTimeout(startIntroAnimation, revealDelayMs)
      }
    })

    if (revealParent.classList.contains('active')) {
      activateTimer = window.setTimeout(startIntroAnimation, revealDelayMs)
    } else {
      classObserver.observe(revealParent, {
        attributes: true,
        attributeFilter: ['class'],
      })
    }

    return () => {
      classObserver.disconnect()
      window.clearTimeout(activateTimer)
      window.clearTimeout(resetTimer)
    }
  }, [])

  return (
    <button
      ref={domRef}
      className={`group flex w-full items-center justify-center py-4 text-sm font-bold uppercase transition-colors ${className} ${
        playOnLoad || (hasIntroCompleted && isVisible) ? 'bracket-active' : ''
      }`}
      onClick={onClick}
      type='button'
    >
      <div className='relative flex items-center justify-center'>
        <span className='animate-bracket-left absolute right-full mr-3 text-2xl font-light opacity-0'>[</span>
        <span className='animate-text-fade tracking-[0.2em]'>{children}</span>
        <span className='animate-bracket-right absolute left-full ml-3 text-2xl font-light opacity-0'>]</span>
      </div>
    </button>
  )
}

type PricingCardProps = {
  plan: PricingPlan
  delay: string
  onSubscribe: () => void
}

const PricingCard: React.FC<PricingCardProps> = ({ plan, delay, onSubscribe }) => {
  const featureList = getFeaturesForPlan(plan.id).slice(0, 5)

  const outerBorderClasses = plan.featured
    ? 'bg-[#0052FF]'
    : 'bg-zinc-200 dark:bg-zinc-800 transition-colors duration-500'

  const glowClasses = plan.featured
    ? 'bg-[conic-gradient(from_0deg,transparent_0_90deg,#00d4ff_270deg,#ffffff_360deg)] dark:bg-[conic-gradient(from_0deg,transparent_0_180deg,#4facfe_270deg,#ffffff_360deg)]'
    : 'bg-[conic-gradient(from_0deg,transparent_0_90deg,#0052FF_270deg,#8b5cf6_360deg)] dark:bg-[conic-gradient(from_0deg,transparent_0_180deg,#4facfe_270deg,#0052FF_360deg)]'

  const buttonClassName = plan.featured
    ? 'text-[#0052FF] hover:text-zinc-900 dark:hover:text-white'
    : 'text-zinc-900 dark:text-white hover:text-[#0052FF] dark:hover:text-[#0052FF]'

  return (
    <ScrollReveal delay={delay}>
      <div className='relative flex h-full flex-col group'>
        <div className={`absolute inset-0 overflow-hidden ${outerBorderClasses}`}>
          <div
            className={`absolute top-[-150%] left-[-150%] h-[400%] w-[400%] ${glowClasses} pointer-events-none opacity-0 transition-opacity duration-500 group-hover:animate-[spin_3s_linear_infinite] group-hover:opacity-100`}
          />
        </div>
        <div className='absolute inset-[1px] z-0 bg-white transition-colors duration-500 dark:bg-black' />

        {plan.featured ? (
          <div className='absolute -top-3 left-5 z-20 bg-[#0052FF] px-2 py-0.5'>
            <span className='mono text-[9px] font-bold tracking-widest uppercase text-white'>Most Popular</span>
          </div>
        ) : null}

        <div className='relative z-10 flex h-full flex-col flex-grow p-5'>
          <div className='mb-2 flex items-start justify-between'>
            <div>
              <span className='mono text-xs font-bold tracking-widest text-zinc-600 uppercase dark:text-zinc-300'>
                Tier
              </span>
              <h3 className='mt-1 text-2xl font-black text-zinc-900 uppercase dark:text-white'>{plan.name}</h3>
            </div>
            <span className='mono text-xs font-bold tracking-widest text-zinc-500 uppercase dark:text-zinc-400'>
              {plan.name}
            </span>
          </div>

          <div className='mb-2'>
            <span className='text-2xl font-black text-zinc-900 dark:text-white'>${plan.price.toFixed(2)}</span>
            <span className='mono ml-2 text-[10px] text-zinc-400 uppercase dark:text-zinc-500'>/ month</span>
          </div>

          <p className='mb-4 text-xs text-zinc-500 dark:text-zinc-400'>{plan.description}</p>

          <ul className='mb-5 flex-grow space-y-2'>
            {featureList.map(feature => (
              <li key={feature} className='flex items-start gap-2 text-xs text-zinc-600 dark:text-zinc-300'>
                <span className='mt-0.5 text-[#0052FF]'>*</span>
                <span>{feature}</span>
              </li>
            ))}
          </ul>

          <BracketButton className={buttonClassName} onClick={onSubscribe}>
            {plan.cta}
          </BracketButton>
        </div>
      </div>
    </ScrollReveal>
  )
}

const getStoredTheme = () => {
  if (typeof window === 'undefined') return null
  try {
    const storedTheme = window.localStorage.getItem('theme')
    return storedTheme === 'dark' || storedTheme === 'light' ? storedTheme : null
  } catch {
    return null
  }
}

const getSystemPrefersDark = () => {
  if (typeof window === 'undefined') return true
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false
}

const PaymentPlans: React.FC = () => {
  const navigate = useNavigate()
  const [isDark, setIsDark] = useState(() => {
    const storedTheme = getStoredTheme()
    if (storedTheme) return storedTheme === 'dark'
    return getSystemPrefersDark()
  })
  const [hasExplicitTheme, setHasExplicitTheme] = useState(() => Boolean(getStoredTheme()))

  useEffect(() => {
    if (typeof window === 'undefined') return
    if (hasExplicitTheme) return

    const media = window.matchMedia('(prefers-color-scheme: dark)')
    const handleChange = () => {
      setIsDark(media.matches)
    }

    if (typeof media.addEventListener === 'function') {
      media.addEventListener('change', handleChange)
    } else if (typeof (media as MediaQueryList).addListener === 'function') {
      media.addListener(handleChange)
    }

    return () => {
      if (typeof media.removeEventListener === 'function') {
        media.removeEventListener('change', handleChange)
      } else if (typeof (media as MediaQueryList).removeListener === 'function') {
        media.removeListener(handleChange)
      }
    }
  }, [hasExplicitTheme])

  useEffect(() => {
    if (typeof document === 'undefined') return
    document.documentElement.classList.toggle('dark', isDark)
    if (hasExplicitTheme) {
      window.localStorage.setItem('theme', isDark ? 'dark' : 'light')
    }
  }, [hasExplicitTheme, isDark])

  const toggleTheme = () => {
    setHasExplicitTheme(true)
    setIsDark(current => !current)
  }

  return (
    <section className='relative h-full min-h-screen w-full overflow-y-auto bg-white px-6 pt-20 pb-24 text-zinc-900 transition-colors duration-500 dark:bg-black dark:text-white md:px-12'>
      <style>
        {`
          @keyframes bracket-expand-left {
            0% { transform: translateX(20px); opacity: 0; }
            100% { transform: translateX(0); opacity: 1; }
          }
          @keyframes bracket-expand-right {
            0% { transform: translateX(-20px); opacity: 0; }
            100% { transform: translateX(0); opacity: 1; }
          }
          @keyframes text-fade {
            0% { opacity: 0; }
            100% { opacity: 1; }
          }
          .animate-bracket-left,
          .animate-bracket-right,
          .animate-text-fade {
            opacity: 0;
          }
          .bracket-active .animate-bracket-left {
            animation: bracket-expand-left 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards;
          }
          .bracket-active .animate-bracket-right {
            animation: bracket-expand-right 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards;
          }
          .bracket-active .animate-text-fade {
            animation: text-fade 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards;
          }
        `}
      </style>

      <Navbar onToggleTheme={toggleTheme} isDark={isDark} showInfrastructure={false} />

      <div className='mx-auto max-w-7xl'>
        <ScrollReveal className='mb-6'>
          <span className='mono text-xs font-bold tracking-widest text-[#0052FF] uppercase'>[ Pricing Matrix ]</span>
          <h1 className='mt-2 text-4xl font-black tracking-tighter text-zinc-900 uppercase dark:text-white md:text-5xl'>
            Runtime Plans
          </h1>
          <p className='mt-2 max-w-2xl text-sm leading-relaxed text-zinc-500 dark:text-zinc-400'>
            Select the capacity that matches your workload. Every plan includes the full branching core and model
            routing stack.
          </p>
        </ScrollReveal>

        <div className='mx-auto mb-24 grid max-w-5xl grid-cols-1 gap-6 md:grid-cols-3'>
          <PricingCard plan={PLANS[0]} delay='reveal-delayed-1' onSubscribe={() => navigate('/login')} />
          <PricingCard plan={PLANS[1]} delay='reveal-delayed-2' onSubscribe={() => navigate('/login')} />
          <PricingCard plan={PLANS[2]} delay='reveal-delayed-3' onSubscribe={() => navigate('/login')} />
        </div>

        <ScrollReveal>
          <div className='mx-auto max-w-5xl border border-zinc-200 bg-white p-8 transition-colors duration-500 dark:border-zinc-800 dark:bg-black'>
            <div className='mb-12 flex items-end justify-between'>
              <div>
                <div className='mb-2 flex items-center gap-4'>
                  <div className='h-6 w-1 bg-[#0052FF]' />
                  <span className='mono text-xs tracking-widest text-zinc-400 uppercase dark:text-zinc-500'>
                    Comparison Grid
                  </span>
                </div>
                <h2 className='ml-5 text-2xl font-bold text-zinc-900 uppercase dark:text-white'>Plan Matrix</h2>
              </div>
              <span className='mono text-sm font-bold tracking-widest text-[#0052FF] uppercase'>Billed Monthly</span>
            </div>

            <div className='overflow-x-auto'>
              <table className='w-full min-w-[720px] border-collapse text-left'>
                <thead>
                  <tr className='border-b border-zinc-200 dark:border-zinc-800'>
                    <th className='mono w-1/4 px-4 py-4 text-[10px] font-normal tracking-widest text-zinc-400 uppercase dark:text-zinc-500'>
                      Feature
                    </th>
                    {PLANS.map(plan => (
                      <th
                        key={plan.id}
                        className='mono w-1/4 px-4 py-4 text-center text-[10px] font-bold tracking-widest text-zinc-900 uppercase dark:text-white'
                      >
                        {plan.name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className='text-sm'>
                  {PRICING_FEATURES.map((row, index) => {
                    const isLast = index === PRICING_FEATURES.length - 1

                    return (
                      <tr
                        key={row.name}
                        className={`${isLast ? '' : 'border-b border-zinc-200 dark:border-zinc-800/50'} transition-colors hover:bg-zinc-50 dark:hover:bg-zinc-900/30`}
                      >
                        <td className='px-4 py-5 font-medium text-zinc-600 dark:text-zinc-300'>{row.name}</td>
                        {PLANS.map(plan => (
                          <td key={plan.id} className='px-4 py-5 text-center text-zinc-500 dark:text-zinc-400'>
                            {row.plans[plan.id] === 'Not included' || row.plans[plan.id] === 'x' ? '—' : row.plans[plan.id]}
                          </td>
                        ))}
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </ScrollReveal>
      </div>

      <Footer isDark={isDark} />
    </section>
  )
}

export default PaymentPlans
